import React, { useEffect, useRef, useState } from 'react';

interface AnimatedHeightProps {
	/**
	 * A key identifying the current content. Changing it triggers a re-measure so
	 * the height animates to the new content (e.g. a wizard step or a view swap).
	 */
	step: string;
	children: React.ReactNode;
	className?: string;
}

/**
 * Smoothly animates its height as the content changes. Measures the content's
 * natural height and transitions to it, so swapping between differently-sized
 * views (wizard steps, a modal entering crop mode) grows/shrinks instead of
 * snapping.
 */
export function AnimatedHeight({ step, children, className }: AnimatedHeightProps) {
	const contentRef = useRef<HTMLDivElement>(null);
	const [height, setHeight] = useState<number | 'auto'>('auto');

	useEffect(() => {
		const el = contentRef.current;
		if (!el) return;
		const frame = requestAnimationFrame(() => {
			setHeight(el.scrollHeight);
		});
		return () => cancelAnimationFrame(frame);
	}, [step]);

	useEffect(() => {
		const el = contentRef.current;
		if (!el) return;
		const observer = new ResizeObserver(() => {
			setHeight(el.scrollHeight);
		});
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	return (
		<div
			className={className ?? 'overflow-hidden transition-[height] duration-200 ease-out'}
			style={{ height: height === 'auto' ? 'auto' : height }}
		>
			{/* `flow-root`, so the measurement is of everything.
			    Without it this div has no padding or border, so a first child's
			    `margin-top` COLLAPSES out of it: the margin lands inside the clipping
			    box above (a BFC of its own, being `overflow-hidden`) while
			    `scrollHeight` reports the content without it. The height set is then
			    short by exactly that margin and the bottom of the content is cut off.
			    `flow-root` makes this a block formatting context, so the child's
			    margin is contained and measured. Same for a last child's
			    `margin-bottom`. */}
			<div ref={contentRef} className="flow-root">
				{children}
			</div>
		</div>
	);
}
